import type { LabelingClassification } from '@erp/rpc';

/**
 * What Browse all should say about the classifier's verdict on one email.
 *
 * Three states, kept apart on purpose:
 *
 *   `unclassified` — no verdict row exists at all.
 *   `skipped`      — the classifier produced no verdict: nothing read the email (no
 *                    supplier resolved, no config, matching off), or the model failed
 *                    to answer. Its stored `not_849` is a placeholder, not an answer,
 *                    and showing it as a decision would recreate the exact conflation
 *                    this surface exists to end. Only the recorded reason separates
 *                    the unread case from the failed-to-answer one — see **Skipped**
 *                    in `docs/ship-and-debit/CONTEXT.md`.
 *   `classified`   — a real verdict someone can argue with.
 */
/** The decisions and bands a REAL verdict can carry, named so a renderer's lookup
 *  table breaks the build when the contract gains a new one. */
export type ClassifiedDecision = LabelingClassification['decision'];
export type ClassifiedBand = LabelingClassification['band'];

export type ClassifierVerdictDisplay =
  | { kind: 'unclassified' }
  | { kind: 'skipped'; reason: string | null }
  | {
      kind: 'classified';
      decision: ClassifiedDecision;
      /**
       * Null when the row carries no probability.
       *
       * Its band was computed on a different quantity, so repeating it would assert
       * something nobody measured. An unknown is NOT a low one — the point of
       * carrying the null all the way from the column to the screen.
       */
      band: ClassifiedBand | null;
      reason: string | null;
    };

export function classifierVerdictDisplay(
  classification: LabelingClassification | null,
): ClassifierVerdictDisplay {
  if (!classification) return { kind: 'unclassified' };

  if (classification.method === 'skipped') {
    return { kind: 'skipped', reason: classification.reason };
  }

  return {
    kind: 'classified',
    decision: classification.decision,
    band: classification.probability === null ? null : classification.band,
    reason: classification.reason,
  };
}
